/**
 * LoginPage — form column of the Option A split shell.
 *
 * Backend contract unchanged: posts to `authService.login(email, password)`.
 * Account-status checks (deleted / suspended / inactive) preserved verbatim.
 *
 * UI consumes system primitives on the surface (no glass): AuthShell +
 * AuthHeading + FormField + Input + Button.
 */

import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineLockClosed,
} from 'react-icons/hi2'
import { useAuth } from '../../contexts/AuthContext'
import { authService } from '../../services/authService'
import { Button, FormField, Input, IconButton, Banner } from '../../components/ui'
import { AuthShell, AuthGlassCard, AuthHeading } from '../../components/auth'

const loginSchema = z.object({
  email:    z.string().min(1, 'Email is required').email('Invalid email format'),
  password: z.string().min(1, 'Password is required').min(6, 'Password must be at least 6 characters'),
})

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [apiError, setApiError] = useState('')

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  })

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      setApiError('')

      const response = await authService.login(data.email, data.password)

      if (response.success) {
        const { user, tokens } = response.data.data

        // Preserve the account-status gate exactly as the previous page had it
        if (user.account) {
          const account = user.account
          if (account.is_deleted) {
            setApiError('Your account has been deleted. Please contact support for assistance.')
            return
          }
          if (account.is_disabled) {
            const reason = account.disabled_reason
            setApiError(reason
              ? `Your account has been suspended. Reason: ${reason}`
              : 'Your account has been suspended. Please contact support for assistance.')
            return
          }
          if (!account.is_active) {
            setApiError('Your account is inactive. Please contact support for assistance.')
            return
          }
        }

        const loginSuccess = login(user, tokens)
        if (loginSuccess) {
          if (user.role === 'superadmin') {
            navigate('/superadmin', { replace: true })
          } else if (user.role === 'assistant') {
            navigate('/superadmin/communications', { replace: true })
          } else {
            navigate('/dashboard', { replace: true })
          }
        } else {
          setApiError('Failed to process login data')
        }
      } else {
        setApiError(response.error?.message || 'Login failed')
      }
    } catch {
      setApiError('Network error - please try again')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthShell variant="centered">
      <AuthGlassCard className="mx-auto">
        <AuthHeading
          title={t('auth.login.title')}
          subtitle={t('auth.login.subtitle')}
        />

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 max-w-md mx-auto">
          {apiError && (
            <Banner tone="danger" title="Sign in failed">
              {apiError}
            </Banner>
          )}

          <FormField
            label={t('auth.login.email')}
            error={errors.email?.message}
          >
            <Input
              type="email"
              autoComplete="email"
              placeholder={t('auth.login.emailPlaceholder')}
              size="lg"
              {...register('email')}
            />
          </FormField>

          <FormField
            label={t('auth.login.password')}
            error={errors.password?.message}
          >
            <Input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder={t('auth.login.passwordPlaceholder')}
              size="lg"
              leadingIcon={HiOutlineLockClosed}
              trailingAction={
                <IconButton
                  icon={showPassword ? HiOutlineEyeSlash : HiOutlineEye}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPassword(s => !s)}
                />
              }
              {...register('password')}
            />
          </FormField>

          <div className="flex justify-end -mt-2">
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-brand hover:text-brand-hover hover:underline underline-offset-4 transition-colors"
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </div>

          <Button type="submit" size="lg" fullWidth loading={isLoading}>
            {t('auth.login.submit')}
          </Button>

          <p className="text-center text-sm text-ink-secondary">
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" className="font-semibold text-brand hover:text-brand-hover transition-colors">
              {t('auth.login.registerLink')}
            </Link>
          </p>
        </form>
      </AuthGlassCard>
    </AuthShell>
  )
}
